import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { END_POINT, METHODS } from "../../../helper/helper";
import { fetchData } from "../../../helper/ApiCall";
import Swal from "sweetalert2";

const RegisterClient = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");

  const getStrength = (p) => {
    if (p.length === 0) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const strength = getStrength(password);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
  const strengthColor = ["", "#EF4444", "#F59E0B", "#3B82F6", "#10B981"];

  const LoginHandler = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      const raw = JSON.stringify({ name, email, password });
      const result = await fetchData(END_POINT.RegisterClient, METHODS.POST, raw);
      if (result.success) {
        Swal.fire({
          title: "Account Created!",
          text: result.message,
          icon: "success",
          confirmButtonColor: "#4F46E5",
          confirmButtonText: "Go to Login",
        }).then((r) => { if (r.isConfirmed) navigate("/login"); });
      } else {
        Swal.fire({
          title: "Registration Failed",
          text: result.message,
          icon: "error",
          confirmButtonColor: "#4F46E5",
          confirmButtonText: "Try Again",
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: "100%",
    padding: "12px 16px 12px 42px",
    fontSize: "14px",
    border: `1.5px solid ${focused === field ? "#4F46E5" : "#E8ECF4"}`,
    borderRadius: "10px",
    outline: "none",
    background: focused === field ? "#FAFBFF" : "#F7F8FC",
    color: "#1A202C",
    fontFamily: "inherit",
    transition: "all 0.2s",
    boxShadow: focused === field ? "0 0 0 3px rgba(79,70,229,0.1)" : "none",
    boxSizing: "border-box",
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Inter', system-ui, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #A0AEC0; }
      `}</style>

      {/* BG orbs */}
      <div style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: "-10%", left: "-10%", width: "50vw", height: "50vw", borderRadius: "50%", background: "rgba(255,255,255,0.06)", filter: "blur(60px)" }} />
        <div style={{ position: "absolute", bottom: "-10%", right: "-5%", width: "40vw", height: "40vw", borderRadius: "50%", background: "rgba(255,255,255,0.05)", filter: "blur(50px)" }} />
      </div>

      {/* Card */}
      <div style={{
        position: "relative", zIndex: 1,
        background: "#FFFFFF", borderRadius: "24px",
        padding: "40px 36px 36px",
        width: "100%", maxWidth: "420px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        animation: "fadeUp 0.4s ease both",
      }}>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "10px",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            borderRadius: "16px", padding: "10px 20px", marginBottom: "20px",
          }}>
            <span style={{ fontSize: "22px" }}>🤖</span>
            <span style={{ fontSize: "22px", fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>AI Hub</span>
          </div>
          <h1 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "6px" }}>Create your account</h1>
          <p style={{ fontSize: "13px", color: "#94a3b8" }}>Join thousands of AI enthusiasts today</p>
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "20px" }}>

          {/* Name */}
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", marginBottom: "6px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Full Name</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>👤</span>
              <input
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => setFocused("name")}
                onBlur={() => setFocused("")}
                style={inputStyle("name")}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", marginBottom: "6px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Email Address</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>✉️</span>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused("")}
                style={inputStyle("email")}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", marginBottom: "6px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Password</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>🔒</span>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocused("password")}
                onBlur={() => setFocused("")}
                style={{ ...inputStyle("password"), paddingRight: "44px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "2px" }}
              >{showPassword ? "🙈" : "👁️"}</button>
            </div>

            {/* Strength meter */}
            {password.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
                  {[1, 2, 3, 4].map((lvl) => (
                    <div key={lvl} style={{
                      flex: 1, height: "3px", borderRadius: "2px",
                      background: strength >= lvl ? strengthColor[strength] : "#E8ECF4",
                      transition: "background 0.25s",
                    }} />
                  ))}
                </div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: strengthColor[strength] }}>
                  {strengthLabel[strength]}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={LoginHandler}
          disabled={loading || !name.trim() || !email.trim() || !password.trim()}
          style={{
            width: "100%", padding: "13px",
            background: loading || !name.trim() || !email.trim() || !password.trim()
              ? "#C7D2FE"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff", border: "none", borderRadius: "12px",
            fontSize: "15px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "inherit", transition: "all 0.2s",
            boxShadow: loading ? "none" : "0 4px 16px rgba(102,126,234,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            marginBottom: "20px",
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 8px 24px rgba(102,126,234,0.5)"; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 4px 16px rgba(102,126,234,0.4)"; }}
        >
          {loading ? (
            <>
              <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Creating account…
            </>
          ) : "Create Account →"}
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
          <div style={{ flex: 1, height: "1px", background: "#E8ECF4" }} />
          <span style={{ fontSize: "12px", color: "#A0AEC0" }}>Already have an account?</span>
          <div style={{ flex: 1, height: "1px", background: "#E8ECF4" }} />
        </div>

        {/* Back to login */}
        <div style={{ textAlign: "center" }}>
          <Link
            to="/login"
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              color: "#4F46E5", fontWeight: 600, fontSize: "14px",
              textDecoration: "none", padding: "10px 20px",
              borderRadius: "10px", border: "1.5px solid #C7D2FE",
              background: "#EEF2FF", transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#E0E7FF"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#EEF2FF"; }}
          >
            ← Back to Login
          </Link>
        </div>

        {/* Terms note */}
        <p style={{ textAlign: "center", fontSize: "11px", color: "#A0AEC0", marginTop: "20px", lineHeight: "1.6" }}>
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </>
  );
};

export default RegisterClient;